import { IDeliveryCalculationPvz } from 'models/deliveryCalculator'
import { IDeliverySubsidyRule } from 'models/logisticsSettings'
import { IPickupPoint } from 'models/pickupPoint'
import { IProductResponse } from 'models/product'

export interface ICalculatorLine {
  product: IProductResponse
  count: number
}

export const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

export const formatDeliveryPeriod = (min?: number, max?: number): string => {
  if (min == null && max == null) return '—'
  if (min === max || max == null) return `${min} дн.`
  return `${min}–${max} дн.`
}

export const formatDeliveryWeight = (grams: number): string =>
  grams >= 1000
    ? `${Number((grams / 1000).toFixed(3)).toLocaleString('ru-RU')} кг`
    : `${Number(grams.toFixed(0)).toLocaleString('ru-RU')} г`

export const getDeliveryPointAddress = (
  point:
    | IPickupPoint
    | IDeliveryCalculationPvz
    | Omit<IDeliveryCalculationPvz, 'provider'>
): string =>
  point.location.address_full ?? point.location.address ?? point.name ?? '—'

export const getProductPrice = (product: IProductResponse): number =>
  Number(product.f_price ?? product.price ?? 0)

export const getBasketOriginalTotal = (lines: ICalculatorLine[]): number =>
  roundMoney(
    lines.reduce(
      (sum, line) => sum + Number(line.product.price ?? 0) * line.count,
      0
    )
  )

export const getBasketDefaultTotal = (lines: ICalculatorLine[]): number =>
  roundMoney(
    lines.reduce(
      (sum, line) => sum + getProductPrice(line.product) * line.count,
      0
    )
  )

/** Распределяет общий чек по строкам и гарантирует точную сумму в последней. */
export const distributeCheck = (
  lines: ICalculatorLine[],
  total: number
): number[] => {
  if (!lines.length) return []
  const safeTotal = roundMoney(Math.max(0, total))
  const weights = lines.map(
    (line) => getProductPrice(line.product) * line.count
  )
  const weightsTotal = weights.reduce((sum, value) => sum + value, 0)
  const fallbackTotal = lines.reduce((sum, line) => sum + line.count, 0)
  let distributed = 0

  return lines.map((line, index) => {
    if (index === lines.length - 1) return roundMoney(safeTotal - distributed)
    const ratio = weightsTotal
      ? weights[index] / weightsTotal
      : line.count / fallbackTotal
    const value = roundMoney(safeTotal * ratio)
    distributed = roundMoney(distributed + value)
    return value
  })
}

export const getLineWeightKg = (
  item: Pick<
    IProductResponse,
    'dimension'
  > | { dimension: { weight: number; unit?: 'KG' | 'GR' } },
  count: number
): number => {
  const weight = Number(item.dimension?.weight ?? 0)
  const unit = item.dimension?.unit
  return (unit === 'GR' ? weight / 1000 : weight) * count
}

export const getPricePerKg = (
  discountedPrice: number,
  weightKg: number
): number | null =>
  weightKg > 0 ? roundMoney(discountedPrice / weightKg) : null

export const toCalculationPvz = (
  point: IPickupPoint
): IDeliveryCalculationPvz => ({
  provider: point.provider,
  code: point.code || point.uuid || '',
  name: point.name,
  location: {
    latitude: point.location.latitude,
    longitude: point.location.longitude,
    address:
      point.location.address ?? point.location.address_full ?? point.name,
    ...(point.location.address_full && {
      address_full: point.location.address_full
    }),
    ...(point.location.city_code !== undefined && {
      city_code: point.location.city_code
    })
  }
})

export interface ISubsidyDiagnostics {
  ruleAllowance: number | null
  policyAllowance: number
  effectiveAllowance: number
  percentLimitApplied: boolean
  deliveryCostLimitApplied: boolean
}

export const getSubsidyDiagnostics = (
  sellerCommission: number,
  providerCost: number,
  rule: IDeliverySubsidyRule | null,
  policyLimitPercent: number
): ISubsidyDiagnostics => {
  const policyAllowance = roundMoney(
    sellerCommission * policyLimitPercent / 100
  )
  const ruleAllowance = rule
    ? roundMoney(
      sellerCommission * rule.commission_compensation_percent / 100
    )
    : null
  const effectiveAllowance = Math.min(
    policyAllowance,
    ruleAllowance ?? policyAllowance
  )
  return {
    ruleAllowance,
    policyAllowance,
    effectiveAllowance,
    percentLimitApplied:
      !!rule &&
      policyLimitPercent < rule.commission_compensation_percent,
    deliveryCostLimitApplied: effectiveAllowance > providerCost
  }
}
